import { useState, useEffect } from "react";
import { apiUrl } from "../config/api";

export function AdminDashboard() {
  const [stats, setStats] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [scans, setScans] = useState<any[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [webhooks, setWebhooks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"stats" | "users" | "scans" | "teams" | "webhooks">("stats");

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, usersRes, scansRes, teamsRes, webhooksRes] = await Promise.all([
        fetch(apiUrl("/admin/stats"), { credentials: "include" }),
        fetch(apiUrl("/admin/users"), { credentials: "include" }),
        fetch(apiUrl("/admin/scans"), { credentials: "include" }),
        fetch(apiUrl("/admin/teams"), { credentials: "include" }),
        fetch(apiUrl("/admin/webhooks"), { credentials: "include" })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
      if (scansRes.ok) {
        const data = await scansRes.json();
        setScans(Array.isArray(data) ? data : data.scans || []);
      }
      if (teamsRes.ok) setTeams(await teamsRes.json());
      if (webhooksRes.ok) setWebhooks(await webhooksRes.json());
    } catch (err) {
      setError("Failed to load admin data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const deleteScan = async (scanId: string) => {
    try {
      const res = await fetch(apiUrl(`/admin/scans/${scanId}`), {
        method: "DELETE",
        credentials: "include"
      });
      if (res.ok) {
        setScans(scans.filter(s => s.scanId !== scanId));
      }
    } catch (err) {
      setError("Failed to delete scan");
      console.error(err);
    }
  };

  const containerStyle: React.CSSProperties = {
    padding: "20px",
    backgroundColor: "var(--bg-primary)",
    color: "var(--text-primary)",
    maxWidth: "1400px",
    margin: "0 auto"
  };

  const tabStyle = (isActive: boolean): React.CSSProperties => ({
    padding: "10px 20px",
    backgroundColor: isActive ? "var(--nav-active-bg)" : "var(--nav-inactive-bg)",
    color: isActive ? "var(--nav-active-text)" : "var(--nav-inactive-text)",
    border: "none",
    borderRadius: "6px 6px 0 0",
    cursor: "pointer",
    marginRight: "5px",
    fontSize: "14px"
  });

  const cardStyle: React.CSSProperties = {
    padding: "20px",
    backgroundColor: "var(--nav-inactive-bg)",
    borderRadius: "8px",
    border: "1px solid var(--border-color)",
    marginBottom: "20px"
  };

  const statCardStyle: React.CSSProperties = {
    padding: "20px",
    backgroundColor: "var(--nav-inactive-bg)",
    borderRadius: "8px",
    border: "1px solid var(--border-color)",
    textAlign: "center",
    flex: "1"
  };

  const tableStyle: React.CSSProperties = {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "14px"
  };

  const tableHeaderStyle: React.CSSProperties = {
    padding: "10px",
    backgroundColor: "var(--nav-active-bg)",
    textAlign: "left",
    fontWeight: "500",
    borderBottom: "2px solid var(--border-color)"
  };

  const tableCellStyle: React.CSSProperties = {
    padding: "10px",
    borderBottom: "1px solid var(--border-color)"
  };

  if (loading) {
    return <div style={containerStyle}>Loading admin dashboard...</div>;
  }

  if (error) {
    return <div style={containerStyle}>Error: {error}</div>;
  }

  return (
    <div style={containerStyle}>
      <h1 style={{ fontSize: "24px", marginBottom: "20px" }}>Admin Dashboard</h1>

      <div style={{ marginBottom: "20px", display: "flex", gap: "5px", borderBottom: "1px solid var(--border-color)" }}>
        <button onClick={() => setActiveTab("stats")} style={tabStyle(activeTab === "stats")}>
          Stats
        </button>
        <button onClick={() => setActiveTab("users")} style={tabStyle(activeTab === "users")}>
          Users
        </button>
        <button onClick={() => setActiveTab("scans")} style={tabStyle(activeTab === "scans")}>
          Scans
        </button>
        <button onClick={() => setActiveTab("teams")} style={tabStyle(activeTab === "teams")}>
          Teams
        </button>
        <button onClick={() => setActiveTab("webhooks")} style={tabStyle(activeTab === "webhooks")}>
          Webhooks
        </button>
      </div>

      {activeTab === "stats" && stats && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px" }}>
          <div style={statCardStyle}>
            <div style={{ fontSize: "24px", fontWeight: "bold" }}>{stats.totalUsers}</div>
            <div style={{ fontSize: "12px", opacity: 0.7 }}>Total Users</div>
          </div>
          <div style={statCardStyle}>
            <div style={{ fontSize: "24px", fontWeight: "bold" }}>{stats.totalScans}</div>
            <div style={{ fontSize: "12px", opacity: 0.7 }}>Total Scans</div>
          </div>
          <div style={statCardStyle}>
            <div style={{ fontSize: "24px", fontWeight: "bold" }}>
              {Math.round(stats.uptime / 3600)}h
            </div>
            <div style={{ fontSize: "12px", opacity: 0.7 }}>Server Uptime</div>
          </div>
        </div>
      )}

      {activeTab === "users" && (
        <div style={cardStyle}>
          <h2 style={{ fontSize: "18px", marginBottom: "15px" }}>Users ({users.length})</h2>
          <div style={{ overflowX: "auto" }}>
            <table style={tableStyle}>
              <thead>
                <tr style={{ backgroundColor: "var(--nav-active-bg)" }}>
                  <th style={tableHeaderStyle}>Email</th>
                  <th style={tableHeaderStyle}>Role</th>
                  <th style={tableHeaderStyle}>Scans</th>
                  <th style={tableHeaderStyle}>Created</th>
                  <th style={tableHeaderStyle}>Last Login</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.email}>
                    <td style={tableCellStyle}>{user.email}</td>
                    <td style={tableCellStyle}>
                      <span style={{
                        padding: "4px 8px",
                        backgroundColor: user.role === "admin" ? "var(--secondary-color)" : "var(--border-color)",
                        borderRadius: "4px",
                        fontSize: "12px"
                      }}>
                        {user.role}
                      </span>
                    </td>
                    <td style={tableCellStyle}>{user.scanCount}</td>
                    <td style={tableCellStyle}>{new Date(user.createdAt).toLocaleDateString()}</td>
                    <td style={tableCellStyle}>{user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : "Never"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "scans" && (
        <div style={cardStyle}>
          <h2 style={{ fontSize: "18px", marginBottom: "15px" }}>Scans ({scans.length})</h2>
          <div style={{ overflowX: "auto" }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={tableHeaderStyle}>Scan ID</th>
                  <th style={tableHeaderStyle}>Owner</th>
                  <th style={tableHeaderStyle}>Profile</th>
                  <th style={tableHeaderStyle}>Timestamp</th>
                  <th style={tableHeaderStyle}>Action</th>
                </tr>
              </thead>
              <tbody>
                {scans.map(scan => (
                  <tr key={scan.scanId}>
                    <td style={tableCellStyle}>{scan.scanId.substring(0, 16)}...</td>
                    <td style={tableCellStyle}>{scan.ownerEmail || "Anonymous"}</td>
                    <td style={tableCellStyle}>{scan.profile}</td>
                    <td style={tableCellStyle}>{new Date(scan.timestamp).toLocaleString()}</td>
                    <td style={tableCellStyle}>
                      <button
                        onClick={() => deleteScan(scan.scanId)}
                        style={{
                          padding: "4px 8px",
                          backgroundColor: "var(--error-color)",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "12px"
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "teams" && (
        <div style={cardStyle}>
          <h2 style={{ fontSize: "18px", marginBottom: "15px" }}>Teams ({teams.length})</h2>
          {teams.length === 0 ? (
            <p style={{ opacity: 0.7 }}>No teams created</p>
          ) : (
            <div style={{ display: "grid", gap: "10px" }}>
              {teams.map(team => (
                <div key={team.id} style={{
                  padding: "12px",
                  backgroundColor: "var(--bg-primary)",
                  borderRadius: "6px",
                  border: "1px solid var(--border-color)"
                }}>
                  <div style={{ fontWeight: "500" }}>{team.name}</div>
                  <div style={{ fontSize: "12px", opacity: 0.7 }}>
                    Owner: {team.ownerEmail} | Members: {team.members.length}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "webhooks" && (
        <div style={cardStyle}>
          <h2 style={{ fontSize: "18px", marginBottom: "15px" }}>Webhooks ({webhooks.length})</h2>
          {webhooks.length === 0 ? (
            <p style={{ opacity: 0.7 }}>No webhooks configured</p>
          ) : (
            <div style={{ display: "grid", gap: "10px" }}>
              {webhooks.map(webhook => (
                <div key={webhook.id} style={{
                  padding: "12px",
                  backgroundColor: "var(--bg-primary)",
                  borderRadius: "6px",
                  border: "1px solid var(--border-color)"
                }}>
                  <div style={{ fontWeight: "500" }}>{webhook.url}</div>
                  <div style={{ fontSize: "12px", opacity: 0.7 }}>
                    Owner: {webhook.ownerEmail} | Status: {webhook.active ? "Active" : "Disabled"} | Failures: {webhook.failCount}
                  </div>
                  <div style={{ fontSize: "12px", opacity: 0.7 }}>
                    Events: {webhook.events.join(", ")}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
